import { v4 as uuidv4 } from 'uuid';
import { CompositorNodeV3 } from '@/types/nodes/compositor';
import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import type {
  Alignment,
  BlendMode,
  Color,
  Font,
  HorizontalAlign,
  Layer,
  LayerId,
  NodeId,
  Orientation,
  ToolMode,
  VerticalAlign,
} from 'web';

export class CompositorView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  private applyLayerEdit(layerId: LayerId, apply: (layer: Layer) => Partial<Layer> | undefined, ongoing = false) {
    // cancelOngoingAction before reading from the graph outside an edit
    this.graph.cancelOngoingAction();
    const layer = this.getLayer(layerId);
    const newLayer = apply(layer);
    if (newLayer === undefined) {
      return;
    }

    this.graph.wasmEdit((wasm) => {
      return wasm.updateLayer(this.nodeId, layerId, { ...layer, ...newLayer });
    }, ongoing);
  }

  getLayer(layerId: LayerId): Layer {
    const compositorData = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    return compositorData.layers[layerId];
  }

  zoomLevel(): number {
    return this.graph.getUIState().compositor[this.nodeId]?.zoomLevel ?? 1;
  }

  setZoomLevel(zoom: number) {
    this.graph.wasm.call((wasm) => {
      wasm.setZoomLevel(this.nodeId, zoom);
    });
  }

  zoomToFit() {
    this.graph.wasm.call((wasm) => {
      wasm.zoomToFit(this.nodeId);
    });
  }

  toolMode(): ToolMode {
    return this.graph.getUIState().compositor[this.nodeId]?.toolMode ?? 'selection';
  }

  setToolMode(mode: ToolMode) {
    this.graph.wasm.call((wasm) => wasm.setToolMode(this.nodeId, mode));
  }

  setStageDimensions(width: number, height: number) {
    this.graph.wasmEdit((wasm) => wasm.setStageDimensions(this.nodeId, width, height));
  }

  setCompositorDuration(duration: number) {
    this.graph.wasmEdit((wasm) => wasm.setCompositorDuration(this.nodeId, duration));
  }

  setCompositorFps(fps: number) {
    this.graph.wasmEdit((wasm) => wasm.setCompositorFps(this.nodeId, fps));
  }

  setIsCompositorTimelineEnabled(enabled: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setIsCompositorTimelineEnabled(this.nodeId, enabled));
  }

  setIsLayerLocked(layerId: LayerId, locked: boolean) {
    this.applyLayerEdit(layerId, (layer) => (layer.locked === locked ? undefined : { locked }), false);
  }

  setLayerPositionX(layerId: LayerId, x: number) {
    this.applyLayerEdit(
      layerId,
      (layer) => (Math.abs(layer.position.x - x) < 0.0001 ? undefined : { position: { x, y: layer.position.y } }),
      false,
    );
  }

  setLayerPositionY(layerId: LayerId, y: number) {
    this.applyLayerEdit(
      layerId,
      (layer) => (Math.abs(layer.position.y - y) < 0.0001 ? undefined : { position: { x: layer.position.x, y } }),
      false,
    );
  }

  setLayerRotation(layerId: LayerId, angle: number) {
    this.applyLayerEdit(
      layerId,
      (layer) => {
        return Math.abs(layer.rotation - angle) < 0.0001 ? undefined : { rotation: angle };
      },
      false,
    );
  }

  setLayerWidth(layerId: LayerId, width: number) {
    this.applyLayerEdit(
      layerId,
      (layer) => {
        return Math.abs(layer.dimension.x - width) < 0.0001
          ? undefined
          : {
              dimension: {
                x: width,
                y: layer.lockedAspectRatio ? (layer.dimension.y / layer.dimension.x) * width : layer.dimension.y,
              },
            };
      },
      false,
    );
  }

  setLayerHeight(layerId: LayerId, height: number) {
    this.applyLayerEdit(
      layerId,
      (layer) => {
        return Math.abs(layer.dimension.y - height) < 0.0001
          ? undefined
          : {
              dimension: {
                x: layer.lockedAspectRatio ? (layer.dimension.x / layer.dimension.y) * height : layer.dimension.x,
                y: height,
              },
            };
      },
      false,
    );
  }

  setLayerColor(layerId: LayerId, value: Color, ongoing = false) {
    this.applyLayerEdit(layerId, (layer) => (layer.color === value ? undefined : { color: value }), ongoing);
  }

  setLayerBlendMode(layerId: LayerId, blendMode: BlendMode, ongoing: boolean) {
    this.applyLayerEdit(layerId, (layer) => (layer.blendMode === blendMode ? undefined : { blendMode }), ongoing);
  }

  setLayerVisible(layerId: LayerId, visible: boolean) {
    this.applyLayerEdit(layerId, (layer) => (layer.visible === visible ? undefined : { visible }), false);
  }

  setLayerMuted(layerId: LayerId, muted: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        (layer.kind.type !== 'video' && layer.kind.type !== 'audio') || layer.kind.muted === muted
          ? undefined
          : { kind: { ...layer.kind, muted } },
      false,
    );
  }

  setLayerStartTime(layerId: LayerId, startTime: number, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) => (Math.abs((layer.startTime ?? 0) - startTime) < 0.0001 ? undefined : { startTime }),
      ongoing,
    );
  }

  setLayerDuration(layerId: LayerId, duration: number, ongoing: boolean) {
    const clampedDuration = Math.max(duration, 0);
    this.applyLayerEdit(
      layerId,
      (layer) =>
        Math.abs((layer.duration ?? 5) - clampedDuration) < 0.0001 ? undefined : { duration: clampedDuration },
      ongoing,
    );
  }

  setVideoLayerTrimStart(layerId: LayerId, trimStart: number, ongoing: boolean) {
    const clampedTrimStart = Math.max(trimStart, 0);
    this.applyLayerEdit(
      layerId,
      (layer) => {
        return (layer.kind.type !== 'video' && layer.kind.type !== 'audio') ||
          Math.abs(layer.kind.trimStart - clampedTrimStart) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, trimStart: clampedTrimStart } };
      },
      ongoing,
    );
  }

  setVideoLayerTrimEnd(layerId: LayerId, trimEnd: number, ongoing: boolean) {
    const clampedTrimEnd = Math.max(trimEnd, 0);
    this.applyLayerEdit(
      layerId,
      (layer) =>
        (layer.kind.type !== 'video' && layer.kind.type !== 'audio') ||
        Math.abs(layer.kind.trimEnd - clampedTrimEnd) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, trimEnd: clampedTrimEnd } },
      ongoing,
    );
  }

  setLayerTrim(
    layerId: LayerId,
    updates: { startTime: number; duration: number; trimStart?: number; trimEnd?: number },
    ongoing: boolean,
  ) {
    this.applyLayerEdit(
      layerId,
      (layer) => {
        const partial: Partial<Layer> = {};
        const clampedDuration = Math.max(updates.duration, 0);
        if (Math.abs((layer.duration ?? 5) - clampedDuration) >= 0.0001) {
          partial.duration = clampedDuration;
        }
        const clampedStartTime = Math.max(updates.startTime, 0);
        if (Math.abs((layer.startTime ?? 0) - clampedStartTime) >= 0.0001) {
          partial.startTime = clampedStartTime;
        }
        if (layer.kind.type === 'video' || layer.kind.type === 'audio') {
          const trimStart = updates.trimStart != null ? Math.max(updates.trimStart, 0) : undefined;
          const trimEnd = updates.trimEnd != null ? Math.max(updates.trimEnd, 0) : undefined;
          const kindUpdated =
            (trimStart != null && Math.abs(layer.kind.trimStart - trimStart) >= 0.0001) ||
            (trimEnd != null && Math.abs(layer.kind.trimEnd - trimEnd) >= 0.0001);
          if (kindUpdated) {
            partial.kind = {
              ...layer.kind,
              ...(trimStart != null ? { trimStart } : {}),
              ...(trimEnd != null ? { trimEnd } : {}),
            };
          }
        }
        return Object.keys(partial).length > 0 ? partial : undefined;
      },
      ongoing,
    );
  }

  setLayerTextSize(layerId: LayerId, size: number, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || Math.abs(layer.kind.layoutOptions.size - size) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, size } } },
      ongoing,
    );
  }

  setLayerTextLetterSpacing(layerId: LayerId, letterSpacing: number, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || Math.abs(layer.kind.layoutOptions.letterSpacing - letterSpacing) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, letterSpacing } } },
      ongoing,
    );
  }

  setLayerTextLineHeight(layerId: LayerId, lineHeight: number, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || Math.abs(layer.kind.layoutOptions.lineHeight - lineHeight) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, lineHeight } } },
      ongoing,
    );
  }

  setLayerTextHorizontalAlign(layerId: LayerId, horizontalAlign: HorizontalAlign, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || layer.kind.layoutOptions.horizontalAlign === horizontalAlign
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, horizontalAlign } } },
      ongoing,
    );
  }

  setLayerTextVerticalAlign(layerId: LayerId, verticalAlign: VerticalAlign, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || layer.kind.layoutOptions.verticalAlign === verticalAlign
          ? undefined
          : { kind: { ...layer.kind, layoutOptions: { ...layer.kind.layoutOptions, verticalAlign } } },
      ongoing,
    );
  }

  setLayerTextFont(layerId: LayerId, font: Font, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' ||
        (layer.kind.fontOptions.font.type === font.type && layer.kind.fontOptions.font.data === font.data)
          ? undefined
          : { kind: { ...layer.kind, fontOptions: { ...layer.kind.fontOptions, font } } },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  setLayerTextWeight(layerId: LayerId, weight: number, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || Math.abs(layer.kind.fontOptions.weight - weight) < 0.0001
          ? undefined
          : { kind: { ...layer.kind, fontOptions: { ...layer.kind.fontOptions, weight } } },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  setLayerTextItalic(layerId: LayerId, italic: boolean, ongoing: boolean) {
    this.applyLayerEdit(
      layerId,
      (layer) =>
        layer.kind.type !== 'text' || layer.kind.fontOptions.italic === italic
          ? undefined
          : { kind: { ...layer.kind, fontOptions: { ...layer.kind.fontOptions, italic } } },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  /** Sets text font, weight, and italic in a single WASM edit (one undo step). */
  setLayerTextFontFace(layerId: LayerId, font: Font, weight: number, italic: boolean, ongoing = false) {
    this.applyLayerEdit(
      layerId,
      (layer) => {
        if (layer.kind.type !== 'text') {
          return undefined;
        }
        const fo = layer.kind.fontOptions;
        return {
          kind: {
            ...layer.kind,
            fontOptions: {
              ...fo,
              font,
              weight,
              italic,
            },
          },
        };
      },
      ongoing,
    );
    this.graph.loadFontResources();
  }

  italicSupport(layerId: LayerId): boolean[] {
    const layer = this.getLayer(layerId);

    if (layer.kind.type !== 'text') {
      return [];
    }
    const font = layer.kind.fontOptions;
    return (
      this.graph.wasm.call((wasm) => {
        const support = wasm.italicSupport(font);
        return Object.values(support).map((b) => b === 1);
      }) ?? []
    );
  }

  weightSupport(layerId: LayerId): number[] {
    const layer = this.getLayer(layerId);

    if (layer.kind.type !== 'text') {
      return [];
    }
    const font = layer.kind.fontOptions;
    return (
      this.graph.wasm.call((wasm) => {
        return Array.from(wasm.weightSupport(font));
      }) ?? []
    );
  }

  setLayerOrder(layerId: LayerId, index: number) {
    this.graph.wasmEdit((wasm) => wasm.setLayerOrder(this.nodeId, layerId, index));
  }

  removeLayers(layerIds: LayerId[]) {
    this.graph.edit(() => {
      const undoEntry = this.graph.wasmChange((wasm) => {
        return wasm.removeLayers(this.nodeId, layerIds);
      });
      undoEntry.add(
        UndoRedoEntry.fromNodes([
          {
            type: 'update',
            node: { ...this.graph.store.getNode(this.nodeId) },
          },
        ]),
      );
      return undoEntry;
    });
  }

  selectedLayersIds(): LayerId[] {
    const selectedLayerId = this.graph.getUIState().compositor[this.nodeId]?.selectedLayer;
    return selectedLayerId !== undefined ? [selectedLayerId] : [];
  }

  setSelectedLayerId(layerId: LayerId | undefined) {
    this.graph.wasm.call((wasm) => {
      wasm.setSelectedLayers(this.nodeId, layerId !== undefined ? [layerId] : []);
    });
  }

  updateLayerName(layerId: LayerId, name: string) {
    this.applyLayerEdit(layerId, (layer) => (layer.name === name ? undefined : { name }));
  }

  updateLayerLockedAspectRatio(layerId: LayerId, lockedAspectRatio: boolean) {
    this.applyLayerEdit(layerId, (layer) =>
      layer.lockedAspectRatio === lockedAspectRatio ? undefined : { lockedAspectRatio },
    );
  }

  flipLayer(layerId: LayerId, orientation: Orientation) {
    this.graph.wasmEdit((wasm) => wasm.flipCompositorLayer(this.nodeId, layerId, orientation));
  }

  /** Clockwise 90° in compositor view space (right-handed Z: negative increment). */
  rotateLayer90Clockwise(layerId: LayerId) {
    const tau = 2 * Math.PI;
    this.applyLayerEdit(layerId, (layer) => {
      const next = (((layer.rotation - Math.PI / 2) % tau) + tau) % tau;
      return { rotation: next };
    });
  }

  alignLayer(layerId: LayerId, alignment: Alignment) {
    this.graph.wasmEdit((wasm) => wasm.alignCompositorLayer(this.nodeId, layerId, alignment));
  }

  addNodeInputHandle() {
    this.graph.edit(() => {
      return this.addNodeInputHandleInternal().undoEntry;
    });
  }

  addNodeInputHandleWithKey(): { key: string; undoEntry: UndoRedoEntry } {
    return this.addNodeInputHandleInternal();
  }

  private addNodeInputHandleInternal(): { key: string; undoEntry: UndoRedoEntry } {
    const { input } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    let order = input.length;
    if (!input.some(([key, _]) => key === 'background')) {
      order = order + 1;
    }
    const newInputKey = `layer_${order}`;

    const undoEntry = this.graph.wasmChange((wasm) => wasm.addInputHandle(this.nodeId, newInputKey));

    undoEntry.add(
      this.graph.addNodeInputHandle(this.nodeId, newInputKey, {
        description: '',
        label: newInputKey,
        format: 'uri',
        id: uuidv4(),
        order,
        required: false,
      }),
    );

    return { key: newInputKey, undoEntry };
  }

  enableNodeEditor() {
    this.graph.wasm.call((wasm) => wasm.enableNodeEditor(this.nodeId));
    this.setToolMode('selection');
    this.zoomToFit();
  }

  disableNodeEditor() {
    this.graph.wasm.call((wasm) => wasm.disableNodeEditor());
  }

  renderResult() {
    this.graph.updateNodeOutputs(this.nodeId);
  }

  cancelOngoingAction() {
    this.graph.cancelOngoingAction();
    this.graph.loadFontResources();
  }

  undo() {
    this.graph.undo();
  }

  redo() {
    this.graph.redo();
  }

  clearUndoRedoHistory() {
    this.graph.clearUndoRedoHistory();
  }

  setUndoBarrier() {
    this.graph.setUndoBarrier();
  }

  disableUndoBarrier() {
    this.graph.disableUndoBarrier();
  }

  isVideoInputConnected(): boolean {
    const { input } = this.graph.store.getNodeData<CompositorNodeV3>(this.nodeId).data;
    return (
      input?.some(([_, data]) => {
        if (!data) return false;
        const type = this.graph.nodeOutputType(data.nodeId, data.outputId);
        return type === 'video' || type === 'audio';
      }) ?? false
    );
  }
}
